import { getStageColor, getStageLabel, stageOrder } from '@/components/Deals/deals-constants'
import type { DealStage } from '@/types'

/** Etapa do funil no dashboard (API usa enum Prisma em UPPER_SNAKE). */
export type FunnelStageRow = {
  stage: string
  count: number
  value: number
}

/**
 * Ordem do funil (topo → base):
 * Negociação Futura no topo, fluxo comercial no meio, Ganho na base.
 */
export const SALES_FUNNEL_MAIN_ORDER: readonly string[] = [
  'FECHAMENTO_FUTURO',
  'QUALIFICADO',
  'REUNIAO_AGENDADA',
  'REUNIAO_REALIZADA',
  'PROPOSTA_ELABORACAO',
  'PROPOSTA_ENVIADA',
  'RENEGOCIACAO',
  'GERAR_CONTRATO',
  'CONTRATO_ENVIADO',
  'GANHO',
]

/** Etapas fora do funil principal. */
export const SALES_FUNNEL_EXTRA_ORDER: readonly string[] = ['PAUSADO', 'PERDIDO']

export function normalizeFunnelStageKey(stage: string): string {
  return stage.trim().toUpperCase().replace(/\s+/g, '_')
}

function toDealStageLoose(stage: string): DealStage | null {
  const key = normalizeFunnelStageKey(stage).toLowerCase() as DealStage
  if (stageOrder.includes(key)) return key
  return null
}

export function getFunnelStageLabel(stage: string): string {
  const dealStage = toDealStageLoose(stage)
  if (dealStage) return getStageLabel(dealStage)
  return stage.replace(/_/g, ' ')
}

export function getFunnelStageColor(stage: string): string {
  const dealStage = toDealStageLoose(stage)
  if (dealStage) return getStageColor(dealStage)
  return 'var(--brand-primary)'
}

/** Larguras (%) no topo de cada degrau do funil — 100 no topo, ~34 na base. */
export function buildFunnelWidthStops(
  stepCount: number,
  maxWidth = 100,
  minWidth = 34,
): number[] {
  if (stepCount <= 0) return [maxWidth]
  return Array.from({ length: stepCount + 1 }, (_, i) => {
    const t = i / stepCount
    return Math.round(maxWidth - (maxWidth - minWidth) * t)
  })
}

/** Clip-path de trapézio: topo mais largo, base mais estreita (afunilamento). */
export function getTrapezoidClipPath(topWidthPct: number, bottomWidthPct: number): string {
  const topInset = (100 - topWidthPct) / 2
  const topRight = topInset + topWidthPct
  const bottomInset = (100 - bottomWidthPct) / 2
  const bottomRight = bottomInset + bottomWidthPct
  return `polygon(${topInset}% 0, ${topRight}% 0, ${bottomRight}% 100%, ${bottomInset}% 100%)`
}

export function sortFunnelStages(rows: FunnelStageRow[]): {
  main: FunnelStageRow[]
  extra: FunnelStageRow[]
} {
  const byStage = new Map<string, FunnelStageRow>()
  for (const row of rows) {
    const key = normalizeFunnelStageKey(row.stage)
    const existing = byStage.get(key)
    if (existing) {
      existing.count += row.count
      existing.value += row.value
    } else {
      byStage.set(key, { stage: key, count: row.count, value: row.value })
    }
  }

  const main = SALES_FUNNEL_MAIN_ORDER.map((stage) => ({
    stage,
    count: byStage.get(stage)?.count ?? 0,
    value: byStage.get(stage)?.value ?? 0,
  }))

  const extra = SALES_FUNNEL_EXTRA_ORDER.map((stage) => ({
    stage,
    count: byStage.get(stage)?.count ?? 0,
    value: byStage.get(stage)?.value ?? 0,
  })).filter((s) => s.count > 0)

  const known = new Set([...SALES_FUNNEL_MAIN_ORDER, ...SALES_FUNNEL_EXTRA_ORDER])
  for (const [stage, row] of byStage) {
    if (!known.has(stage) && row.count > 0) {
      extra.push(row)
    }
  }

  return { main, extra }
}
